import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Grid, TextField, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import api from '../utils/axios';

const BLANK_FORM = {
    first_name: '', last_name: '', middle_name: '',
    date_of_birth: '', gender: '', ssn: '', email: '',
    phone: '', address_line1: '', address_line2: '',
    city: '', state: '', zip_code: '', country: 'USA', mrn: '',
};

const BLANK_INSURANCE = {
    insurance_company: '',
    group_number: '',
    policy_number: '',
    subscriber_relationship: 'self',
    subscriber_member_id: '',
    subscriber_first_name: '',
    subscriber_last_name: '',
    subscriber_middle_name: '',
    subscriber_date_of_birth: '',
    subscriber_gender: '',
    subscriber_address_line_1: '',
    subscriber_address_line_2: '',
    subscriber_city: '',
    subscriber_state: '',
    subscriber_zip_code: '',
    subscriber_phone: '',
};

const STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
    'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
    'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
    'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
    'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

const PatientFormDialog = ({
    open,
    onClose,
    onPatientCreated,
    patient = null,
    loading = false,
    onLoadingChange,
}) => {
    const [formData, setFormData] = useState(BLANK_FORM);
    const [insuranceData, setInsuranceData] = useState(BLANK_INSURANCE);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            const primaryInsurance = patient?.primary_insurance || {};
            if (patient) {
                setFormData({
                    first_name: patient.first_name || '',
                    last_name: patient.last_name || '',
                    middle_name: patient.middle_name || '',
                    date_of_birth: patient.date_of_birth || '',
                    gender: patient.gender || '',
                    ssn: patient.ssn || '',
                    email: patient.email || '',
                    phone: patient.phone || '',
                    address_line1: patient.address_line1 || '',
                    address_line2: patient.address_line2 || '',
                    city: patient.city || '',
                    state: patient.state || '',
                    zip_code: patient.zip_code || '',
                    country: patient.country || 'USA',
                    mrn: patient.mrn || '',
                });
                setInsuranceData({
                    ...BLANK_INSURANCE,
                    insurance_company: primaryInsurance.insurance_company || '',
                    group_number: primaryInsurance.group_number || '',
                    policy_number: primaryInsurance.policy_number || '',
                    subscriber_relationship: primaryInsurance.subscriber_relationship || 'self',
                    subscriber_member_id: primaryInsurance.subscriber_member_id || '',
                    subscriber_first_name: primaryInsurance.subscriber_first_name || '',
                    subscriber_last_name: primaryInsurance.subscriber_last_name || '',
                    subscriber_middle_name: primaryInsurance.subscriber_middle_name || '',
                    subscriber_date_of_birth: primaryInsurance.subscriber_date_of_birth || '',
                    subscriber_gender: primaryInsurance.subscriber_gender || '',
                    subscriber_address_line_1: primaryInsurance.subscriber_address_line_1 || '',
                    subscriber_address_line_2: primaryInsurance.subscriber_address_line_2 || '',
                    subscriber_city: primaryInsurance.subscriber_city || '',
                    subscriber_state: primaryInsurance.subscriber_state || '',
                    subscriber_zip_code: primaryInsurance.subscriber_zip_code || '',
                    subscriber_phone: primaryInsurance.subscriber_phone || '',
                });
            } else {
                setFormData(BLANK_FORM);
                setInsuranceData(BLANK_INSURANCE);
            }
            setError('');
        }
    }, [open, patient]);

    const isEdit = Boolean(patient);
    const dialogTitle = isEdit ? 'Edit Patient' : 'Create New Patient';

    const set = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));
    const setInsurance = (field) => (e) => setInsuranceData(prev => ({ ...prev, [field]: e.target.value }));

    const buildPayload = () => {
        const hasInsurance =
            Boolean(patient?.primary_insurance) ||
            Object.entries(insuranceData).some(([key, value]) => key !== 'subscriber_relationship' && value);
        if (!hasInsurance) return formData;

        const insurance = { ...insuranceData };
        if (insurance.subscriber_relationship === 'self') {
            delete insurance.subscriber_first_name;
            delete insurance.subscriber_last_name;
            delete insurance.subscriber_middle_name;
            delete insurance.subscriber_date_of_birth;
            delete insurance.subscriber_gender;
            delete insurance.subscriber_address_line_1;
            delete insurance.subscriber_address_line_2;
            delete insurance.subscriber_city;
            delete insurance.subscriber_state;
            delete insurance.subscriber_zip_code;
            delete insurance.subscriber_phone;
        } else if (!insurance.subscriber_date_of_birth) {
            delete insurance.subscriber_date_of_birth;
        }
        return { ...formData, insurance };
    };

    const handleSubmit = async () => {
        if (onLoadingChange) onLoadingChange(true);
        try {
            const payload = buildPayload();
            const response = isEdit
                ? await api.patch(`/patients/${patient.patient_id}/`, payload)
                : await api.post('/patients/', payload);
            setError('');
            if (onPatientCreated) onPatientCreated(response.data);
            onClose();
        } catch (err) {
            const data = err.response?.data;
            const message =
                (Array.isArray(data?.non_field_errors) && data.non_field_errors[0]) ||
                data?.detail || data?.error || err.message;
            setError(typeof message === 'string' ? message : 'Operation failed');
        } finally {
            if (onLoadingChange) onLoadingChange(false);
        }
    };

    const handleClose = () => {
        setFormData(BLANK_FORM);
        setInsuranceData(BLANK_INSURANCE);
        setError('');
        onClose();
    };

    const isValid =
        formData.first_name && formData.last_name && formData.date_of_birth &&
        formData.gender && formData.address_line1 && formData.city &&
        formData.state && formData.zip_code;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="First Name *" inputProps={{ 'aria-label': 'First Name' }}
                            value={formData.first_name} onChange={set('first_name')} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Middle Name" inputProps={{ 'aria-label': 'Middle Name' }}
                            value={formData.middle_name} onChange={set('middle_name')} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Last Name *" inputProps={{ 'aria-label': 'Last Name' }}
                            value={formData.last_name} onChange={set('last_name')} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Date of Birth *" type="date"
                            inputProps={{ 'aria-label': 'Date of Birth' }}
                            InputLabelProps={{ shrink: true }}
                            value={formData.date_of_birth} onChange={set('date_of_birth')} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel id="gender-label">Gender *</InputLabel>
                            <Select labelId="gender-label" value={formData.gender} label="Gender *"
                                onChange={set('gender')}>
                                <MenuItem value="male">Male</MenuItem>
                                <MenuItem value="female">Female</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="MRN" inputProps={{ 'aria-label': 'MRN' }}
                            value={formData.mrn} onChange={set('mrn')} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="SSN" placeholder="XXX-XX-XXXX"
                            inputProps={{ 'aria-label': 'SSN' }}
                            value={formData.ssn} onChange={set('ssn')} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Email" type="email"
                            inputProps={{ 'aria-label': 'Email' }}
                            value={formData.email} onChange={set('email')} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Phone" inputProps={{ 'aria-label': 'Phone' }}
                            value={formData.phone} onChange={set('phone')} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Address Line 1 *"
                            inputProps={{ 'aria-label': 'Address Line 1' }}
                            value={formData.address_line1} onChange={set('address_line1')} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Address Line 2"
                            inputProps={{ 'aria-label': 'Address Line 2' }}
                            value={formData.address_line2} onChange={set('address_line2')} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="City *" inputProps={{ 'aria-label': 'City' }}
                            value={formData.city} onChange={set('city')} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                            <InputLabel id="state-label">State *</InputLabel>
                            <Select labelId="state-label" value={formData.state} label="State *"
                                onChange={set('state')}>
                                {STATES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField fullWidth label="ZIP Code *"
                            inputProps={{ 'aria-label': 'ZIP Code' }}
                            value={formData.zip_code} onChange={set('zip_code')} />
                    </Grid>
                    <Grid item xs={12}>
                        <div style={{
                            marginTop: 8,
                            paddingTop: 16,
                            borderTop: '1px solid var(--ab-border-hair)',
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--ab-fg-subtle)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                        }}>
                            Insurance & Subscriber
                        </div>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Insurance Company"
                            inputProps={{ 'aria-label': 'Insurance Company' }}
                            value={insuranceData.insurance_company} onChange={setInsurance('insurance_company')} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Member ID"
                            inputProps={{ 'aria-label': 'Member ID' }}
                            value={insuranceData.subscriber_member_id} onChange={setInsurance('subscriber_member_id')} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Group Number"
                            inputProps={{ 'aria-label': 'Group Number' }}
                            value={insuranceData.group_number} onChange={setInsurance('group_number')} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Policy Number"
                            inputProps={{ 'aria-label': 'Policy Number' }}
                            value={insuranceData.policy_number} onChange={setInsurance('policy_number')} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel id="subscriber-relationship-label">Subscriber Relationship</InputLabel>
                            <Select
                                labelId="subscriber-relationship-label"
                                value={insuranceData.subscriber_relationship}
                                label="Subscriber Relationship"
                                onChange={setInsurance('subscriber_relationship')}
                            >
                                <MenuItem value="self">Self</MenuItem>
                                <MenuItem value="spouse">Spouse</MenuItem>
                                <MenuItem value="child">Child</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    {insuranceData.subscriber_relationship !== 'self' && (
                        <>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Subscriber First Name"
                                    inputProps={{ 'aria-label': 'Subscriber First Name' }}
                                    value={insuranceData.subscriber_first_name} onChange={setInsurance('subscriber_first_name')} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Subscriber Last Name"
                                    inputProps={{ 'aria-label': 'Subscriber Last Name' }}
                                    value={insuranceData.subscriber_last_name} onChange={setInsurance('subscriber_last_name')} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Subscriber DOB" type="date"
                                    inputProps={{ 'aria-label': 'Subscriber DOB' }}
                                    InputLabelProps={{ shrink: true }}
                                    value={insuranceData.subscriber_date_of_birth} onChange={setInsurance('subscriber_date_of_birth')} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="subscriber-gender-label">Subscriber Gender</InputLabel>
                                    <Select
                                        labelId="subscriber-gender-label"
                                        value={insuranceData.subscriber_gender}
                                        label="Subscriber Gender"
                                        onChange={setInsurance('subscriber_gender')}
                                    >
                                        <MenuItem value="">Unknown</MenuItem>
                                        <MenuItem value="M">Male</MenuItem>
                                        <MenuItem value="F">Female</MenuItem>
                                        <MenuItem value="U">Other</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Subscriber Address Line 1"
                                    inputProps={{ 'aria-label': 'Subscriber Address Line 1' }}
                                    value={insuranceData.subscriber_address_line_1} onChange={setInsurance('subscriber_address_line_1')} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Subscriber Address Line 2"
                                    inputProps={{ 'aria-label': 'Subscriber Address Line 2' }}
                                    value={insuranceData.subscriber_address_line_2} onChange={setInsurance('subscriber_address_line_2')} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Subscriber City"
                                    inputProps={{ 'aria-label': 'Subscriber City' }}
                                    value={insuranceData.subscriber_city} onChange={setInsurance('subscriber_city')} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField fullWidth label="Subscriber State"
                                    inputProps={{ 'aria-label': 'Subscriber State', maxLength: 2 }}
                                    value={insuranceData.subscriber_state} onChange={setInsurance('subscriber_state')} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField fullWidth label="Subscriber ZIP"
                                    inputProps={{ 'aria-label': 'Subscriber ZIP', maxLength: 10 }}
                                    value={insuranceData.subscriber_zip_code} onChange={setInsurance('subscriber_zip_code')} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Subscriber Phone"
                                    inputProps={{ 'aria-label': 'Subscriber Phone' }}
                                    value={insuranceData.subscriber_phone} onChange={setInsurance('subscriber_phone')} />
                            </Grid>
                        </>
                    )}
                </Grid>
                {error && (
                    <div style={{ color: 'red', fontSize: '0.875rem', marginTop: 16 }}>{error}</div>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={!isValid || loading}>
                    {isEdit ? 'Save Changes' : 'Create Patient'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PatientFormDialog;
